// frontend/src/components/TrailerModal.jsx
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

export default function TrailerModal({ open, onClose, trailer }) {
  const iframeRef = useRef(null);
  const [isPlaying] = useState(true);

  // Fallback UI only when we are confident the embed is blocked
  const [embedBlocked, setEmbedBlocked] = useState(false);

  // More compatible host fallback (some blockers target nocookie specifically)
  const [useStandardHost, setUseStandardHost] = useState(false);

  // Track successful iframe load to avoid false "blocked" UI on slow networks
  const loadedRef = useRef(false);

  const origin = useMemo(() => {
    if (typeof window === "undefined") return "";
    return window.location.origin;
  }, []);

  const canUseJsApi = useMemo(() => {
    // YouTube JS API "origin" param must be a real http(s) origin.
    // On file:// (origin is "null") or other non-http(s) contexts, setting
    // enablejsapi/origin can trigger YouTube "player configuration" errors.
    return typeof origin === "string" && /^https?:\/\//i.test(origin);
  }, [origin]);

  const youtubeWatchUrl = useMemo(() => {
    if (!trailer?.youtubeId) return "";
    return `https://www.youtube.com/watch?v=${trailer.youtubeId}`;
  }, [trailer?.youtubeId]);

  const src = useMemo(() => {
    if (!trailer?.youtubeId) return "";

    // Muted autoplay is the most reliable across browsers; user can unmute in controls.
    const params = new URLSearchParams({
      autoplay: "1",
      mute: "1",
      controls: "1",
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
    });

    if (canUseJsApi) {
      params.set("enablejsapi", "1");
      params.set("origin", origin);
    }

    const host = useStandardHost
      ? "https://www.youtube.com/embed"
      : "https://www.youtube-nocookie.com/embed";

    return `${host}/${trailer.youtubeId}?${params.toString()}`;
  }, [trailer?.youtubeId, origin, useStandardHost]);

  const iframeTargetOrigin = useMemo(() => {
    const host = useStandardHost
      ? "https://www.youtube.com"
      : "https://www.youtube-nocookie.com";
    return host;
  }, [useStandardHost]);

  const sendYTCommand = useCallback((func) => {
    if (!canUseJsApi) return;
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      JSON.stringify({ event: "command", func, args: [] }),
      iframeTargetOrigin
    );
  }, [canUseJsApi, iframeTargetOrigin]);

  const closeAndStop = useCallback(() => {
    // Only works when enablejsapi=1 and origin is valid.
    sendYTCommand("pauseVideo");
    onClose?.();
  }, [onClose, sendYTCommand]);

  // Reset state cleanly whenever opening / changing trailer
  useEffect(() => {
    if (!open) return;
    loadedRef.current = false;
    setEmbedBlocked(false);
    setUseStandardHost(false);
  }, [open, trailer?.youtubeId]);

  // Progressive fallback:
  // - Give nocookie time to load (slow connections exist)
  // - If not loaded, switch to standard host
  // - If still not loaded after a fair time, show fallback UI
  useEffect(() => {
    if (!open || !trailer?.youtubeId) return;

    let cancelled = false;

    const TRY_STANDARD_AFTER_MS = 2200; // was 1200 (too aggressive)
    const SHOW_BLOCKED_AFTER_MS = 5200; // was 2600 (too aggressive)

    const t1 = window.setTimeout(() => {
      if (cancelled) return;
      if (loadedRef.current) return;
      setUseStandardHost(true);
    }, TRY_STANDARD_AFTER_MS);

    const t2 = window.setTimeout(() => {
      if (cancelled) return;
      if (loadedRef.current) return;
      setEmbedBlocked(true);
    }, SHOW_BLOCKED_AFTER_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [open, trailer?.youtubeId]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAndStop();
    };
    document.addEventListener("keydown", onKeyDown);

    // lock scroll
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prev;
    };
  }, [open, closeAndStop]);

  if (!open || !trailer) return null;

  const portalTarget =
    typeof document !== "undefined" ? document.body : null;
  if (!portalTarget) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[99999]"
      role="dialog"
      aria-modal="true"
      aria-label="Trailer player"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) closeAndStop();
      }}
    >
      {/* Dim background (clean) */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-[3px]" />

      {/* Modal shell */}
      <div className="relative h-full w-full flex items-center justify-center p-3 sm:p-6">
        <div className="w-full max-w-5xl">
          {/* Glass card */}
          <div
            className={[
              "rounded-2xl overflow-hidden",
              "border border-white/10",
              "bg-black/65",
              "backdrop-blur-xl",
              "shadow-[0_30px_140px_rgba(0,0,0,0.8)]",
            ].join(" ")}
          >
            {/* Header */}
            <div className="relative px-4 sm:px-5 py-2 border-b border-white/10">
              {/* subtle top sheen */}
              <div className="pointer-events-none absolute inset-0">
                <div className="absolute inset-0 bg-gradient-to-b from-white/[0.06] via-transparent to-transparent" />
                <div className="absolute inset-0 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]" />
              </div>

              <div className="relative flex items-center gap-3">

                <div className="mx-3 hidden sm:block h-6 w-px bg-white/10" />

                {/* Title */}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm sm:text-[15px] font-semibold text-white/90">
                    {trailer.title}
                  </div>
                  <div className="text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-white/45">
                    Trailer
                  </div>
                </div>

                {/* Close */}
                <button
                  type="button"
                  onClick={closeAndStop}
                  className={[
                    "h-9 w-9 inline-flex items-center justify-center rounded-xl",
                    "border border-white/12 bg-black/25",
                    "text-white/70 hover:text-white",
                    "hover:border-white/20 hover:bg-black/35",
                    "transition",
                    "focus:outline-none focus:ring-2 focus:ring-emerald-300/20",
                  ].join(" ")}
                  aria-label="Close trailer"
                  title="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Player */}
            <div className="relative w-full bg-black">
              <div className="relative w-full pt-[56.25%] bg-black group">
                {!embedBlocked ? (
                  <iframe
                    ref={iframeRef}
                    className="absolute inset-0 h-full w-full"
                    src={src}
                    title={trailer.title}
                    allow="autoplay; encrypted-media; picture-in-picture"
                    referrerPolicy="origin-when-cross-origin"
                    allowFullScreen
                    onLoad={() => {
                      loadedRef.current = true;
                      setEmbedBlocked(false);
                    }}
                  />
                ) : (
                  // Fallback UI (still clean, same aesthetic)
                  <div className="absolute inset-0 flex items-center justify-center p-6">
                    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-black/50 backdrop-blur-xl p-4 sm:p-5">
                      <div className="text-sm font-semibold text-white/90">
                        Trailer blocked by your browser
                      </div>
                      <div className="mt-1 text-[12px] text-white/55">
                        This usually happens with ad-blockers or privacy settings.
                        You can still watch it on YouTube.
                      </div>

                      <div className="mt-4 flex flex-wrap gap-2">
                        <a
                          href={youtubeWatchUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="h-10 inline-flex items-center justify-center rounded-xl border border-white/12 bg-white/[0.04] px-4 text-[12px] font-semibold text-white/80 hover:text-white hover:border-white/20 hover:bg-white/[0.06] transition"
                        >
                          Open on YouTube
                        </a>

                        <button
                          type="button"
                          onClick={() => {
                            // Force the more compatible host and retry
                            loadedRef.current = false;
                            setEmbedBlocked(false);
                            setUseStandardHost(true);
                          }}
                          className="h-10 inline-flex items-center justify-center rounded-xl border border-white/12 bg-black/25 px-4 text-[12px] font-semibold text-white/70 hover:text-white hover:border-white/20 hover:bg-black/35 transition"
                        >
                          Try Again
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* subtle bottom fade (clean, not flashy) */}
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/35 via-black/10 to-transparent opacity-70" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute inset-0 bg-black/[0.06]" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer (minimal + brand) */}
            <div className="px-4 sm:px-5 py-3 border-t border-white/10">
              <div className="flex items-center gap-2 text-[11px] text-white/45">
                <span className="tracking-[0.24em] uppercase">AnimeHouse</span>
                <span className="h-1 w-1 rounded-full bg-white/20" />
                <span className="tracking-[0.22em] uppercase">Player</span>
                <span className="ml-auto text-white/35">
                  {isPlaying ? "" : ""}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>,
    portalTarget
  );
}
